import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaCalendarDay } from '@react-icons/all-files/fa/FaCalendarDay';
import { FaUmbrellaBeach } from '@react-icons/all-files/fa/FaUmbrellaBeach';
import { FaCalendarAlt } from '@react-icons/all-files/fa/FaCalendarAlt';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import { FaPlus } from '@react-icons/all-files/fa/FaPlus';
import Breadcrumb from '../../../../../commons/Breadcrumb';
import PageHeader from '../../../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../../../commons/PageAlert';
import Confirm, { useConfirm } from '../../../../../commons/Confirm';
import { EditButton, DeleteButton, RenderActions } from '../../../../../commons/DataGridActions';
import ProfaseDataGrid from '../../../../../commons/Datagrid';
import { useAppDispatch, useAppSelector } from '../../../../../store';
import { getEntities, deleteEntity, reset } from '../../../../../manager/calendario-dia-festivo.reducer';

const BASE = '/configuracion/calendario/feriados/calendario-festivos';
const FERIADOS = '/configuracion/calendario/feriados';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const DIAS_SEMANA = ['', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

function fmtMes(mes?: number | null) {
  if (mes == null) return '—';
  return MESES[mes - 1] ?? String(mes);
}

function fmtTipo(tipo?: string | null) {
  if (tipo === 'semana') return 'Por semana';
  if (tipo === 'fecha') return 'Fecha fija';
  return tipo ?? '—';
}

function fmtDia(row: any) {
  if (row.tipo === 'semana') {
    const n = Number(row.dia);
    return DIAS_SEMANA[n] ?? row.dia ?? '—';
  }
  return row.dia ?? '—';
}

function fmtSemana(row: any) {
  if (row.tipo !== 'semana') return '—';
  if (row.semana == null) return '—';
  return `${row.semana}ª`;
}

function reglaLabel(row: any) {
  return row.nombre ?? `#${row.id}`;
}

export default function CalendarioFestivosPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const topRef = useRef<HTMLDivElement>(null);

  const entities = useAppSelector(s => s.calendarioDiaFestivo.entities);
  const totalItems = useAppSelector(s => s.calendarioDiaFestivo.totalItems);
  const loading = useAppSelector(s => s.calendarioDiaFestivo.loading);
  const updating = useAppSelector(s => s.calendarioDiaFestivo.updating);
  const updateSuccess = useAppSelector(s => s.calendarioDiaFestivo.updateSuccess);

  const [rows, setRows] = useState<any[]>([]);
  const [lastParams, setLastParams] = useState<any>(null);

  const { alert, showToast } = usePageAlert();
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  useEffect(() => {
    const msg = (location.state as any)?.successMsg;
    if (msg) { showToast('success', msg); window.history.replaceState({}, ''); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      dismiss();
      if (lastParams) dispatch(getEntities(lastParams));
      showToast('success', 'Regla de calendario eliminada correctamente.');
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setConfirmLoading(updating); }, [updating]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setRows(entities ?? []); }, [entities]);

  const fetchData = (params: any) => {
    setLastParams(params);
    dispatch(getEntities(params));
  };

  const renderActions = (params: any) => (
    <RenderActions>
      <EditButton onClick={() => navigate(`${BASE}/${params.row.id}/edit`)} />
      <DeleteButton onClick={() => {
        ask(
          <>¿Confirmas que deseas eliminar la regla <strong>{reglaLabel(params.row)}</strong>? Esta acción no se puede deshacer.</>,
          () => dispatch(deleteEntity(params.row.id)),
          { title: 'Eliminar regla de calendario', confirmLabel: 'Eliminar', variant: 'danger' },
        );
        topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }} />
    </RenderActions>
  );

  const cols = [
    { field: 'id', headerName: 'ID', flex: 0.45 },
    { field: 'nombre', headerName: 'Nombre', flex: 2.2 },
    {
      field: 'tipo',
      headerName: 'Tipo',
      flex: 1,
      renderCell: (p: any) => fmtTipo(p.row.tipo),
    },
    {
      field: 'mes',
      headerName: 'Mes',
      flex: 1,
      renderCell: (p: any) => fmtMes(p.row.mes),
    },
    {
      field: 'semana',
      headerName: 'Semana',
      flex: 0.7,
      renderCell: (p: any) => fmtSemana(p.row),
    },
    {
      field: 'dia',
      headerName: 'Día',
      flex: 1,
      renderCell: (p: any) => fmtDia(p.row),
    },
    { field: '__actions', headerName: '', flex: 0.8, renderCell: renderActions },
  ];

  return (
    <div style={g.page} ref={topRef}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Calendario', to: '/configuracion/calendario', icon: <FaCalendarAlt /> },
        { label: 'Feriados', to: FERIADOS, icon: <FaUmbrellaBeach /> },
        { label: 'Calendario de Festivos', icon: <FaCalendarDay /> },
      ]} />

      <PageHeader
        icon={<FaCalendarDay />}
        iconColor="#ef4444"
        title="Calendario de Festivos"
        subtitle="Reglas recurrentes para generar días festivos cada año"
        action={{
          label: <><FaPlus style={{ marginRight: 6 }} />Nueva regla</>,
          onClick: () => navigate(`${BASE}/new`),
          color: '#ef4444',
        }}
      />

      <Confirm config={confirmConfig} />
      {alert && <PageAlert alert={alert} />}

      <ProfaseDataGrid
        cols={cols}
        title=""
        filters={[]}
        fetch={fetchData}
        rowCount={totalItems}
        loading={loading}
        rows={rows}
        defaultSortField="mes"
      />
    </div>
  );
}

const g: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },
};
